import axios from 'axios';

import { requestWrapper } from '@/config/requestsConfig';
import { handleError } from '@/utils';

// import Qs from 'qs';

// const codeMessage = {
// 	200: "The server successfully returned the requested data.",
// 	201: "New or modified data is successful.",
// 	202: "A request has entered the background queue (asynchronous task).",
// 	204: "The data was deleted successfully.",
// 	400: "The request was made with an error and the server did not perform any new or modified data operations.",
// 	401: "User does not have permission (token, username, password is incorrect).",
// 	403: "The user is authorized, but access is forbidden.",
// 	404: "The request is made for a record that does not exist and the server does not operate.",
// 	406: "The format of the request is not available.",
// 	410: "The requested resource is permanently deleted and will not be retrieved.",
// 	422: "A validation error occurred when creating an object.",
// 	500: "An error occurred on the server. Please check the server.",
// 	502: "Gateway error.",
// 	503: "The service is unavailable and the server is temporarily overloaded or maintained.",
// 	504: "The gateway timed out.",
// };

// Fake response function to work with stubs
const randomIntFromInterval = (min: number, max: number) =>
  Math.floor(Math.random() * (max - min + 1) + min);

export const fakeResponse = (data: any) =>
  new Promise((resolve) => {
    setTimeout(
      () => {
        resolve(data);
      },
      randomIntFromInterval(200, 600),
    );
  });

export const fakeResponseWithId = <T extends { id: string }>(
  id: string,
  data: T[],
) =>
  new Promise((resolve) => {
    setTimeout(
      () => {
        const res = data.find((d) => d.id === id);
        resolve(res);
      },
      randomIntFromInterval(200, 600),
    );
  });

export const fakeResponseWithDistrictId = <
  T extends { districtId: string; params?: any },
>(
  districtId: string,
  _params: any,
  data: T[],
) =>
  new Promise((resolve) => {
    setTimeout(
      () => {
        const res = data.find((d) => d.districtId === districtId);
        resolve(res);
      },
      randomIntFromInterval(200, 600),
    );
  });

export const getCurrentUserApi = () =>
  requestWrapper
    .get('c13s/users/current_user')
    .then((response) => response.data.data)
    .catch((error) => {
      throw error;
    });

export const getUserPasswordApi = (userEmail: string) =>
  requestWrapper
    .post('c13s/users/reset_password_signature', { userEmail })
    .then((response) => response.data.data)
    .catch((error) => {
      throw error;
    });

export const getUserDataApi = () =>
  requestWrapper
    .post('a12n/refresh/verify')
    .then((response) => response.data)
    .catch((error) => {
      throw error;
    });

export const getUserPreferenceApi = () =>
  requestWrapper
    .get('c13s/user_preferences')
    .then((response) => response.data)
    .catch((error) => {
      throw error;
    });

export const putUserPreferenceApi = (body: {
  defaultLicenceAreaId: string;
  weatherVariable: string;
}) =>
  requestWrapper
    .put('c13s/user_preferences', body)
    .then((response) => response.data)
    .catch(handleError);

// App Config

export const getAppConfigApi = () =>
  requestWrapper
    .get('c13s/config')
    .then((response) => response.data)
    .catch(handleError);

export const getMapShapesApi = () =>
  requestWrapper.get('c13s/config/shapes').then(async (response) => {
    const { shapesUrl, boundariesUrl } = response.data.data;

    const shapes = await axios
      .get(shapesUrl)
      .then((shapesResp) => shapesResp.data);

    const boundaries = await axios
      .get(boundariesUrl)
      .then((boundariesResp) => boundariesResp.data);

    return {
      shapes,
      boundaries,
    };
  });

export const getLicenceAreasApi = (): Promise<ILicenceAreaMin[]> =>
  requestWrapper
    .get('c13s/licence_areas')
    .then((response) => response.data.data)
    .catch((error) => {
      throw error;
    });

export const getLicenceAreaApi = (licenceAreaId: string) =>
  requestWrapper
    .get(`c13s/licence_areas/${licenceAreaId}`)
    .then((response) => response.data.data)
    .catch((error) => {
      throw error;
    });

export const getLicenceAreaFaultBandsApi = (
  licenceAreaId: string,
): Promise<ILicenceAreaFaultBands> =>
  requestWrapper
    .get(`c13s/licence_areas/${licenceAreaId}/faultBands`)
    .then((response) => response.data.data)
    .catch((error) => {
      throw error;
    });

export const getDistrictFaultBandsApi = (districtId: string) =>
  requestWrapper
    .get(`c13s/districts/${districtId}/faultBands`)
    .then((response) => response.data.data)
    .catch((error) => {
      throw error;
    });

export const putDistrictFaultBandsApi = ({
  districtId,
  body,
}: {
  districtId: string;
  body: {
    faultBand24h: {
      firstLowerBand: number;
      secondLowerBand: number;
    };
  };
}) =>
  requestWrapper
    .put(`api/c13s/districts/${districtId}/faultBands`, body)
    .then((response) => response.data)
    .catch(handleError);

export const getLicenceAreaDistrictsApi = (licenceAreaId: string) =>
  requestWrapper
    .get(`c13s/licence_areas/${licenceAreaId}/districts`)
    .then((response) =>
      response.data.data.map(
        (item: { districtId: string; districtName: string }) => ({
          ...item,
          licenceAreaId,
        }),
      ),
    )
    .catch(handleError);

export const getDistrictApi = (
  districtId: string,
): Promise<IDistrictResponse> =>
  requestWrapper
    .get(`c13s/districts/${districtId}`)
    .then((response) => response.data.data)
    .catch(handleError);

export const getDistrictFaultAndWeatherForecastsApi = (
  districtId: string,
  params: {
    horodatetime: string | undefined;
  },
) =>
  requestWrapper
    .get(`c13s/districts/${districtId}/fault_and_weather_forecasts`, { params })
    .then((response) => response.data.data)
    .catch(handleError);

export const getWeatherEventsDetailsApi = (weatherEventId: string) =>
  requestWrapper
    .get(`c13s/weather_events/${weatherEventId}`)
    .then((response) => response.data.data)
    .catch(handleError);

export const getDistrictFaultForecastsApi = (districtId: string, params: any) =>
  requestWrapper
    .get(`c13s/districts/${districtId}/fault_forecast`, { params })
    .then((response) => response.data.data)
    .catch(handleError);

export const getDistrictFaultForecastsByFaultBandApi = (
  districtId: string,
  params: any,
) =>
  requestWrapper
    .get(`c13s/districts/${districtId}/fault_forecast/by/faultBand`, {
      params,
    })
    .then((response) => response.data.data)
    .catch(handleError);

export const getLicenceAreaFaultForecastsApi = (
  licenceAreaId: string,
  params: {
    startDatetime?: string;
    timeResolution?: string;
  },
) =>
  requestWrapper
    .get(`c13s/licence_areas/${licenceAreaId}/fault_forecast`, { params })
    .then((response) => response.data.data)
    .catch(handleError);

export const getLicenceAreaFaultForecastsByFaultBandApi = (
  licenceAreaId: string,
  params: {
    startDatetime?: string;
    timeResolution?: string;
    horodatetime?: string;
  },
) =>
  requestWrapper
    .get(`c13s/licence_areas/${licenceAreaId}/fault_forecast/by/faultBand`, {
      params,
    })
    .then((response) => response.data.data)
    .catch(handleError);

export const getLicenceAreaExtremeWeatherForecastApi = (
  licenceAreaId: string,
  params: {
    targetDatetime?: string;
  },
) =>
  requestWrapper
    .get(`c13s/licence_areas/${licenceAreaId}/weather_forecast/extreme`, {
      params,
    })
    .then((response) => response.data.data)
    .catch(handleError);

export const getWeatherEventSimulationBreakdownApi = (
  licenceAreaId: string,
  weatherEventId: string,
  params: {
    horodatetime?: string;
  },
) =>
  requestWrapper
    .get(
      `c13s/weather_event/${weatherEventId}/licence_area/${licenceAreaId}/districts/fault_forecast/breakdown`,
      {
        params,
      },
    )
    .then((response) => response.data.data)
    .catch(handleError);

export const getWeatherEventSimulationForecastApi = (
  districtId: string,
  weatherEventId: string,
  params: {
    startDatetime: string;
    timeResolution: string;
    horodatetime?: string;
  },
) =>
  requestWrapper
    .get(
      `c13s/weather_event/${weatherEventId}/districts/${districtId}/fault_forecast`,
      { params },
    )
    .then((response) => response.data.data)
    .catch(handleError);

export const getWeatherEventSimulationForecastByFaultBandApi = (
  districtId: string,
  weatherEventId: string,
  params: {
    startDatetime?: string;
    horodatetime?: string;
  },
) =>
  requestWrapper
    .get(
      `c13s/weather_event/${weatherEventId}/districts/${districtId}/fault_forecast/by/faultBand`,
      {
        params,
      },
    )
    .then((response) => response.data.data)
    .catch(handleError);

//GET /districts/<districtId>/weather_forecast/extreme
export const getDistrictExtremeWeatherForecastApi = (
  districtId: string,
  params: {
    targetDatetime?: string;
  },
) =>
  requestWrapper
    .get(`c13s/districts/${districtId}/weather_forecast/extreme`, {
      params,
    })
    .then((response) => response.data.data)
    .catch(handleError);

export const getDistrictSimilarEventsApi = (districtId: string) =>
  requestWrapper
    .get(`c13s/districts/${districtId}/weather_events/by/similarity`)
    .then((response) => response.data.data)
    .catch(handleError);

export const getDistrictsSimilarityApi = (
  weatherEventId: string,
  licenceAreaId: string,
) =>
  requestWrapper
    .get(
      `c13s/weather_events/${weatherEventId}/licence_areas/${licenceAreaId}/similarity/by/districts`,
    )
    .then((response) => response.data.data)
    .catch(handleError);

export const getDistrictHourlyWeatherForecastApi = (
  district_id: string,
  params: {
    startDatetime: string;
    endDatetime: string;
  },
) =>
  requestWrapper
    .get(`c13s/districts/${district_id}/weather_forecasts`, { params })
    .then((response) => response.data.data)
    .catch(handleError);

export const getLicenceAreaDistrictsFaultAndWeatherForecastsBreakdownApi = (
  licenceAreaId: string,
  params: {
    horodatetime: string | undefined;
  },
) =>
  requestWrapper
    .get(
      `c13s/licence_areas/${licenceAreaId}/districts/fault_and_weather_forecasts/breakdown`,
      { params },
    )
    .then((response) => response.data.data)
    .catch(handleError);

export const getLicenceAreaTotalFaultAndWeatherForecastsBreakdownApi = (
  licenceAreaId: string,
  params: {
    horodatetime: string | undefined;
  },
) =>
  requestWrapper
    .get(
      `c13s/licence_areas/${licenceAreaId}/fault_and_weather_forecasts/breakdown`,
      { params },
    )
    .then((response) => response.data.data)
    .catch(handleError);

export const getMarkerHourlyWeatherForecastApi = (
  markerCoordinates: {
    lat: number;
    lng: number;
  },
  params: {
    startDatetime: string;
    endDatetime: string;
    weatherHorodatetime: string;
    weatherVariable: string;
  },
) =>
  requestWrapper
    .get(
      `c13s/weather_forecasts/${markerCoordinates.lat}/${markerCoordinates.lng}`,
      { params },
    )
    .then((response) => response.data.data)
    .catch(handleError);

export const getLicenceAreaRessourceCalculatorParametersApi = (
  licenceAreaId?: string,
): Promise<LicenceAreaResourceCalculatorParameters> =>
  requestWrapper
    .get(`c13s/licence_area/${licenceAreaId}/resource_calculator_parameters`)
    .then((response) => response.data.data)
    .catch(handleError);

export const getDistrictRessourceCalculatorParametersApi = (
  districtId?: string,
): Promise<DistrictResourceCalculatorParameters> =>
  requestWrapper
    .get(`c13s/districts/${districtId}/resource_calculator_parameters`)
    .then((response) => response.data.data)
    .catch(handleError);

export const putRessourceCalculatorParametersApi = ({
  licenceAreaId,
  body,
}: {
  licenceAreaId: string;
  body: any;
}) =>
  requestWrapper
    .put(
      `c13s/licence_area/${licenceAreaId}/resource_calculator_parameters`,
      body,
    )
    .then((response) => response.data)
    .catch(handleError);

export const postResourceCalculatorResultApi = (
  body: ResourceCalculatorParameters,
) =>
  requestWrapper
    .post('c13s/resource_calculator', body)
    .then((response) => response.data)
    .catch(handleError);

export const getWeatherEventsApi = (params: any) =>
  requestWrapper
    .get(`c13s/weather_events?${params}`)
    .then((response) => response.data.data)
    .catch((error) => {
      throw error;
    });

export const getHorodateTimeApi = (targetDatetime: string) =>
  requestWrapper
    .get('c13s/fault_and_weather_forecasts/horodatetimes', {
      params: { targetDatetime },
    })
    .then((response) => response.data.data)
    .catch(handleError);

export const getFaultsBreakdownApi = (
  licenceAreaId: string,
  weatherEventId: string,
) =>
  requestWrapper
    .get(
      `c13s/weather_event/${weatherEventId}/licence_area/${licenceAreaId}/districts/faults/breakdown`,
    )
    .then((response) => response.data.data)
    .catch(handleError);

//  GET - weather events network_impact
export const getNetworkImpactApi = ({
  weatherEventId,
  licenceAreaId,
}: {
  weatherEventId: string | undefined;
  licenceAreaId: string | undefined;
}) =>
  requestWrapper
    .get(
      `c13s/weather_events/${weatherEventId}/licence_area/${licenceAreaId}/network_impact`,
    )
    .then((response) => response.data.data)
    .catch((error) => {
      if (error?.response?.status === 404) {
        throw error;
      }
      handleError(error);
    });

export const getActionPlanApi = (
  licenceAreaId: string,
  weatherEventId: string,
) =>
  requestWrapper
    .get(
      `c13s/weather_events/${weatherEventId}/action_plan?licenceAreaId=${licenceAreaId}`,
    )
    .then((response) => response.data.data)
    .catch(handleError);

// Users Management

export const getUsersApi = () =>
  requestWrapper
    .get('c13s/users')
    .then((response) => response.data.data)
    .catch((error) => {
      throw error;
    });

export const postUserApi = (body: any) =>
  requestWrapper
    .post('c13s/users', body)
    .then((response) => response.data)
    .catch((error) => {
      throw error;
    });

export const putUserApi = ({ email, body }: { email: string; body: any }) =>
  requestWrapper
    .put(`c13s/users/${email}`, body)
    .then((response) => response.data)
    .catch((error) => {
      throw error;
    });

export const deleteUserApi = (userEmail: string) =>
  requestWrapper
    .delete(`c13s/users/${userEmail}`)
    .then((response) => response.data)
    .catch((error) => {
      throw error;
    });

export const unblockUserApi = (userEmail: string) =>
  requestWrapper
    .post(`c13s/users/${userEmail}/unblock`)
    .then((response) => response.data)
    .catch((error) => {
      throw error;
    });

/// Mail Subscription
export const getMailSubscriptionsApi = () =>
  requestWrapper
    .get('c13s/mail_subscriptions')
    .then((response) => response.data.data)
    .catch((error) => {
      throw error;
    });

export const postMailSubscriptionApi = (body: {
  licenceAreaId: string;
  subscriptionType: string;
  deliveryHours?: string[];
}) =>
  requestWrapper
    .post('c13s/mail_subscriptions', body)
    .then((response) => response.data.data)
    .catch((error) => {
      throw error;
    });

export const putMailSubscriptionApi = ({
  id,
  subscriptionType,
  deliveryHours,
}: {
  id: string;
  subscriptionType: string;
  deliveryHours?: string[];
}) =>
  requestWrapper
    .put(`c13s/mail_subscriptions/${id}`, { subscriptionType, deliveryHours })
    .then((response) => response.data.data)
    .catch((error) => {
      throw error;
    });

export const deleteMailSubscriptionApi = (id: string) =>
  requestWrapper
    .delete(`c13s/mail_subscriptions/${id}`)
    .then((response) => response.data.data)
    .catch((error) => {
      throw error;
    });

export const postFeedbackApi = (body: any) =>
  requestWrapper.post('c13s/feedback', body).then((response) => response.data);

// Weather events validation/edition form
export const putWeatherEventsUpdateApi = ({
  weatherEventId,
  body,
}: {
  weatherEventId: string;
  body: any;
}) =>
  requestWrapper
    .put(`c13s/weather_events/${weatherEventId}`, body)
    .then((response) => response.data)
    .catch((error) => {
      throw error;
    });

export const getWeatherEventActionPlanApi = ({ eventId, licenceAreaId }: any) =>
  requestWrapper
    .get(
      `c13s/weather_events/${eventId}/action_plan?licenceAreaId=${licenceAreaId}`,
    )
    .then((response) => response.data)
    .catch(handleError);

export const deleteWeatherEventApi = (weatherEventId: string | number) =>
  requestWrapper
    .delete(`c13s/weather_events/${weatherEventId}`)
    .then((response) => response.data)
    .catch(handleError);

// forecast quality
export const getForecastQualityApi = (params: any) =>
  requestWrapper
    .get(`c13s/forecast_quality?${params}`)
    .then((response) => response.data.data)
    .catch((error) => {
      throw error;
    });

export const getWindParticulesApi = (
  params: {
    weatherHorodatetime?: string;
    datetime?: string;
    horizon?: number;
  },
  endpointUrl: string,
) =>
  requestWrapper
    .get(`c13s/wind_particules/${endpointUrl}`, {
      params,
    })
    .then((response) => response.data);
// .catch(handleError);

export const getWindParticulesJSONApi = (url: string) =>
  requestWrapper
    .get(url)
    .then((response) => response.data)
    .catch(handleError);
